import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { Form, Modal, Row } from "react-bootstrap";

import IconSpinner from "components/IconSpinner";
import { get, keyBy, some } from "lodash";
import { flushErrors } from "actions/errorActions";
import { employerConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { toast } from "react-toastify";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import {
  currentPayrolls,
  deleteEmployerGroup,
  getAllEmployerGroups,
} from "actions/employerActions";
import type { RootState } from "types/state";

import Alert from "components/Alert";
import Button from "components/Button";
import IconSubheader from "components/IconSubheader";
import IconHeader from "components/IconHeader";

// Props a parent passes (original propTypes MINUS redux-injected MINUS `client`).
// `onSubmit` and `loading` were declared in propTypes but never used by the
// component — preserved here faithfully as optional own props.
interface RemoveGroupOwnProps {
  onClose?: () => void;
  onSubmit?: () => void;
  onSuccess?: () => void;
  groupData?: Array<Record<string, unknown>>;
  loading?: boolean;
}

const actions = [
  employerConstants.DELETE_EMPLOYER_GROUPS,
  employerConstants.GET_EMPLOYER_GROUPS,
  employerConstants.CURRENT_PAYROLLS,
];

const errorSelector = createErrorSelector(actions);
const isDeleting = createLoadingSelector(actions);
const isLoading = createLoadingSelector(employerConstants.CURRENT_PAYROLLS);

const mapStateToProps = (state: RootState) => {
  const groups = state.employer.groups;
  const groupsById = keyBy(state.employer.groups, "id");

  return {
    groupsById,
    groups,
    isDeleting: isDeleting(state),
    error: errorSelector(state),
    isFetching: isLoading(state),
    payrolls: state.employer.currentPayrolls,
  };
};

const mapDispatchToProps = {
  getAllEmployerGroups,
  deleteEmployerGroup,
  flushErrors,
  currentPayrolls,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type RemoveGroupProps = PropsFromRedux & RemoveGroupOwnProps;

interface RemoveGroupState {
  groupHasProcessingPayrolls: boolean;
  groupHasApprovedPayrolls: boolean;
}

class RemoveGroup extends React.PureComponent<
  WithApolloClient<RemoveGroupProps>,
  RemoveGroupState
> {
  constructor(props: WithApolloClient<RemoveGroupProps>) {
    super(props);

    this.state = {
      groupHasProcessingPayrolls: false,
      groupHasApprovedPayrolls: false,
    };
  }

  componentDidMount() {
    // React ignores promises from lifecycle methods; kick the async work
    // explicitly (was `async componentDidMount` pre-conversion)
    void this._loadCurrentPayrolls();
  }

  _loadCurrentPayrolls = async () => {
    // Pre-existing bug preserved: `currentPayrolls` requires (client, companyId)
    // but is invoked with only the client — the companyId arg is missing.
    // @ts-expect-error currentPayrolls expects a second companyId argument
    await this.props.currentPayrolls(this.props.client as ApolloClient<object>);
    const approved = this.props.payrolls.filter(
      (payroll) => payroll.status === "APPROVED"
    );
    const processing = this.props.payrolls.filter(
      (payroll) => payroll.status === "PROCESSING"
    );
    const groupId = get(this.props.groupData, "id");
    this.setState({
      groupHasProcessingPayrolls: some(processing, { groupId }),
      groupHasApprovedPayrolls: some(approved, { groupId }),
    });
  };

  _onClose = () => {
    this.props.flushErrors();
    // onClose is an optional own prop invoked unconditionally
    this.props.onClose!();
  };

  _removeGroup = async () => {
    // groupData is Record<string, unknown>[], so lodash `get` widens to
    // `undefined` here; the action's mutation variables expect a string id —
    // cast at the boundary (through unknown, matching the original runtime).
    const id = get(this.props.groupData, "id") as unknown as string;
    const result = await this.props.deleteEmployerGroup(
      this.props.client as ApolloClient<object>,
      {
        id,
      }
    );
    if (result.success) {
      // onSuccess is an optional own prop invoked unconditionally
      this.props.onSuccess!();
      toast.success("Successfully removed group.");
      // onClose is an optional own prop invoked unconditionally
      this.props.onClose!();
    } else {
      toast.error("There was a problem removing the group.");
    }
  };

  render() {
    const id = get(this.props.groupData, "id");
    const groupName = get(this.props.groupData, "name");
    if (this.props.isFetching) {
      return <IconSpinner centered />;
    }

    return (
      <div>
        <Modal
          centered
          scrollable
          show={true}
          onHide={this._onClose}
          className="dismissed-payroll"
        >
          <Modal.Header className="dismissed-payroll-header">
            <div className="payroll-header">
              <IconHeader variant="cardHeader" headerText={"Remove Group"} />
              {this.state.groupHasApprovedPayrolls &&
                !this.state.groupHasProcessingPayrolls && (
                  <IconSubheader subheader="Are you sure you want to remove the following group?" />
                )}
            </div>
          </Modal.Header>

          <Modal.Body>
            <Row className="mb-3">
              <Form.Group className="mb-3">
                {!this.state.groupHasApprovedPayrolls &&
                  !this.state.groupHasProcessingPayrolls && (
                    <div className="dismiss-payroll-info" key={id}>
                      <span className="payroll-label">
                        <p>Group</p>
                      </span>
                      <span>
                        <p>{groupName}</p>
                      </span>
                    </div>
                  )}
                {this.state.groupHasProcessingPayrolls && (
                  <div className="dismiss-payroll-info">
                    <span>
                      <p>
                        {"There are currently payroll contributions processing and you are unable to remove the group." +
                          " Come back in 1 business day, after contributions have processed, to remove the group."}
                      </p>
                    </span>
                  </div>
                )}
                {this.state.groupHasApprovedPayrolls &&
                  !this.state.groupHasProcessingPayrolls && (
                    <div className="dismiss-payroll-info">
                      <span>
                        <p>
                          {"There are currently payroll contributions approved for processing. By continuing and removing this group," +
                            " the payroll contributions will be un-approved. Would you like to continue?"}
                        </p>
                      </span>
                    </div>
                  )}
              </Form.Group>
            </Row>

            {this.props.error && <Alert type="error" msg={this.props.error} />}
          </Modal.Body>
          <Modal.Footer>
            <div className="dismiss-actions">
              <span>
                <Button
                  className="table-btn"
                  name="cancel"
                  color="dismiss"
                  size="sm"
                  btnLabel="Cancel"
                  type="button"
                  onClick={this.props.onClose}
                />
              </span>
              {!this.state.groupHasProcessingPayrolls && (
                <span style={{ paddingLeft: 3 }}>
                  <Button
                    className="table-btn"
                    color="red"
                    size="sm"
                    btnLabel="Remove"
                    name="submit"
                    withArrow={true}
                    loading={this.props.isDeleting}
                    disabled={this.props.isDeleting}
                    onClick={() => {
                      // Pre-existing quirk: an `id` arg is passed but
                      // `_removeGroup` takes none — it re-derives the id from
                      // props internally. `void` because the handler is async.
                      // @ts-expect-error _removeGroup expects 0 arguments
                      void this._removeGroup(id);
                    }}
                  />
                </span>
              )}
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default connector(withApollo<RemoveGroupProps>(RemoveGroup));
